import React, { useState } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { 
  Brain, 
  Cpu, 
  CheckCircle2, 
  ArrowRight,
  Globe,
  Sparkles
} from "lucide-react";
import { 
  Container, 
  Section 
} from "../styles/GlobalStyles";

const PageWrapper = styled(Section)`
  background-color: #0f172a; // Slate 900
  background-image: 
    radial-gradient(circle at 10% 20%, rgba(59, 130, 246, 0.1) 0%, transparent 40%),
    radial-gradient(circle at 90% 80%, rgba(139, 92, 246, 0.1) 0%, transparent 40%);
  min-height: 100vh;
  padding-top: 120px;
  padding-bottom: 80px;
  position: relative;
  overflow: hidden;
`;

const HeaderContainer = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 60px;
  position: relative;
  z-index: 2;
`;

const Title = styled(motion.h1)`
  font-size: 3.5rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  margin-bottom: 24px;
  background: linear-gradient(135deg, #fff 0%, #cbd5e1 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled(motion.p)`
  font-size: 1.25rem;
  color: #94a3b8;
  line-height: 1.6;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    font-size: 1.125rem;
  }
`;

const DiscountBadge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.2);
  padding: 8px 16px;
  border-radius: 9999px;
  color: #60a5fa;
  font-weight: 600;
  font-size: 0.875rem;
  margin-bottom: 24px;
  backdrop-filter: blur(4px);

  svg {
    width: 16px;
    height: 16px;
  }
`;

const TabList = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-bottom: 48px;
  flex-wrap: wrap;
  position: relative;
  z-index: 2;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
    padding: 0 20px;
  }
`;

const TabButton = styled.button`
  position: relative;
  padding: 16px 28px;
  background: ${props => props.active ? 'rgba(255, 255, 255, 0.1)' : 'transparent'};
  border: 1px solid ${props => props.active ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.05)'};
  border-radius: 12px;
  color: ${props => props.active ? '#fff' : '#94a3b8'};
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  backdrop-filter: blur(8px);

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
    transform: translateY(-2px);
  }

  svg {
    width: 18px;
    height: 18px;
    color: ${props => props.active ? '#60a5fa' : 'currentColor'};
  }
`;

const ContentGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: 40px;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const MainCard = styled(motion.div)`
  background: rgba(30, 41, 59, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 24px;
  padding: 40px;
  backdrop-filter: blur(12px);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  }
`;

const SidebarCard = styled(motion.div)`
  background: rgba(30, 41, 59, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 24px;
  padding: 32px;
  height: fit-content;
  backdrop-filter: blur(12px);
`;

const ProgramTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ProgramDescription = styled.p`
  font-size: 1.1rem;
  color: #cbd5e1;
  line-height: 1.7;
  margin-bottom: 32px;
`;

const ModuleList = styled.div`
  display: grid;
  gap: 16px;
`;

const ModuleItem = styled(motion.div)`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
    transform: translateX(5px);
  }

  svg {
    color: #60a5fa;
    width: 20px;
    height: 20px;
    margin-top: 4px;
    flex-shrink: 0;
  }
`;

const ModuleText = styled.div`
  h4 {
    color: #f1f5f9;
    font-weight: 600;
    margin-bottom: 4px;
    font-size: 1rem;
  }
  p {
    color: #94a3b8;
    font-size: 0.9rem;
    line-height: 1.5;
  }
`;

const PriceTag = styled.div`
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  .label {
    display: block;
    color: #94a3b8;
    font-size: 0.9rem;
    margin-bottom: 8px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .amount-wrapper {
    display: flex;
    align-items: baseline;
    gap: 12px;
  }

  .current {
    font-size: 2.5rem;
    font-weight: 800;
    color: #fff;
  }

  .original {
    font-size: 1.25rem;
    color: #64748b;
    text-decoration: line-through;
  }

  .save {
    background: #22c55e;
    color: #fff;
    font-size: 0.75rem;
    font-weight: 700;
    padding: 4px 8px;
    border-radius: 4px;
    margin-left: auto;
  }
`;

const StatRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 24px;

  div {
    text-align: center;
  }

  span {
    display: block;
  }

  .val {
    color: #fff;
    font-weight: 700;
    font-size: 1.25rem;
  }
  .key {
    color: #64748b;
    font-size: 0.8rem;
    margin-top: 4px;
  }
`;

const EnrollButton = styled(motion.button)`
  width: 100%;
  padding: 16px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 8px 20px rgba(37, 99, 235, 0.4);
    transform: translateY(-2px);
  }
`;

const ConsultationButton = styled(motion.button)`
  width: 100%;
  padding: 16px;
  background: transparent;
  color: #94a3b8;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  margin-top: 12px;
  transition: all 0.2s ease;

  &:hover {
    color: #fff;
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.2);
  }
`;

// Data Structure
const pathsData = {
  aiml: {
    id: 'aiml',
    icon: <Brain />,
    title: 'Masters in AI & GenAI',
    shortTitle: 'AI & Machine Learning',
    description: 'Transform your career with the most comprehensive AI program. From mathematical foundations to deploying Large Language Models (LLMs) in production.',
    price: '₹20,000',
    originalPrice: '₹40,000',
    duration: '12 Months',
    projects: '25+ Capstone',
    modules: [
      {
        title: "Foundation & Python for AI",
        desc: "Master Python data stack (NumPy, Pandas) and mathematical concepts essential for ML."
      },
      {
        title: "Deep Learning & Neural Networks",
        desc: "Build and train complex neural networks using TensorFlow and PyTorch."
      },
      {
        title: "Generative AI & LLMs",
        desc: "Work with OpenAI GPT, LangChain, and build custom RAG (Retrieval Augmented Generation) pipelines."
      },
      {
        title: "MLOps & Production",
        desc: "Deploy models using Docker, Kubernetes, and set up monitoring pipelines."
      }
    ],
    color: '#8b5cf6'
  },
  webdev: {
    id: 'webdev',
    icon: <Globe />,
    title: 'Full Stack Engineering',
    shortTitle: 'Web Dev & Systems',
    description: 'Go beyond basic websites. Learn to architect scalable, high-performance distributed systems used by top tech companies.',
    price: '₹10,000',
    originalPrice: '₹20,000',
    duration: '12 Months',
    projects: '20+ Enterprise',
    modules: [
      {
        title: "Advanced Frontend Architecture",
        desc: "React, Next.js, State Management, and Performance Optimization patterns."
      },
      {
        title: "Backend & Microservices",
        desc: "Node.js, Express, System Design, and building scalable microservices."
      },
      {
        title: "Database Engineering",
        desc: "Complex SQL querying, MongoDB aggregation, and Redis caching strategies."
      },
      {
        title: "Cloud & DevOps",
        desc: "AWS infrastructure, CI/CD pipelines, and containerization."
      }
    ],
    color: '#3b82f6'
  },
  iot: {
    id: 'iot',
    icon: <Cpu />,
    title: 'IoT & Embedded Systems',
    shortTitle: 'IoT & Robotics',
    description: 'Bridge the gap between hardware and software. Design smart devices, wearables, and industrial automation systems.',
    price: '₹10,000',
    originalPrice: '₹20,000',
    duration: '12 Months',
    projects: '15+ Hardware',
    modules: [
      {
        title: "Embedded Programming",
        desc: "C/C++ for microcontrollers, Arduino, and ESP32 mastery."
      },
      {
        title: "Circuit Design & Sensors",
        desc: "PCB design basics, sensor integration, and signal processing."
      },
      {
        title: "IoT Communication Protocols",
        desc: "MQTT, CoAP, HTTPs, and secure device-to-cloud communication."
      },
      {
        title: "Robotics & Automation",
        desc: "Motor control, kinematics, and building autonomous bots."
      }
    ],
    color: '#f59e0b'
  }
};

const CareerPaths = () => {
  const [activeTab, setActiveTab] = useState('aiml');
  const navigate = useNavigate();
  const currentPath = pathsData[activeTab];

  return (
    <PageWrapper>
      <Container>
        <HeaderContainer>
          <DiscountBadge
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Sparkles />
            New Year Offer: Flat 50% OFF
          </DiscountBadge>
          
          <Title
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            Engineer Your Future
          </Title>
          
          <Subtitle
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Industry-aligned specialized programs designed to take you from novice to expert.
            Learn by building real-world production systems.
          </Subtitle>
        </HeaderContainer>

        <TabList>
          {Object.values(pathsData).map((path) => (
            <TabButton
              key={path.id}
              active={activeTab === path.id}
              onClick={() => setActiveTab(path.id)}
            >
              {path.icon}
              {path.shortTitle}
            </TabButton>
          ))}
        </TabList>

        <AnimatePresence mode="wait">
          <ContentGrid
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
          >
            {/* Main Content */}
            <MainCard>
              <ProgramTitle>
                <span style={{ color: currentPath.color }}>{currentPath.icon}</span>
                {currentPath.title}
              </ProgramTitle>
              <ProgramDescription>
                {currentPath.description}
              </ProgramDescription>
              
              <h3 style={{ color: '#fff', marginBottom: '20px', fontSize: '1.2rem' }}>Curriculum Highlights</h3>
              <ModuleList>
                {currentPath.modules.map((module, index) => (
                  <ModuleItem 
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <CheckCircle2 size={24} color={currentPath.color} />
                    <ModuleText>
                      <h4>{module.title}</h4>
                      <p>{module.desc}</p>
                    </ModuleText>
                  </ModuleItem>
                ))}
              </ModuleList>
            </MainCard>

            {/* Sidebar / Pricing */}
            <SidebarCard>
              <PriceTag>
                <span className="label">Program Fee (One Time)</span>
                <div className="amount-wrapper">
                  <span className="current">{currentPath.price}</span>
                  <span className="save">SAVE 50%</span>
                </div>
                <span className="original">{currentPath.originalPrice}</span>
              </PriceTag>

              <StatRow>
                <div>
                  <span className="val">{currentPath.duration}</span>
                  <span className="key">Duration</span>
                </div>
                <div>
                  <span className="val">{currentPath.projects}</span>
                  <span className="key">Projects</span>
                </div>
                <div>
                  <span className="val">Live</span>
                  <span className="key">Classes</span>
                </div>
              </StatRow>

              <EnrollButton
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/contact', { state: { program: currentPath.title } })}
                style={{ background: `linear-gradient(135deg, ${currentPath.color} 0%, ${currentPath.color}dd 100%)` }}
              >
                Enroll Now <ArrowRight size={18} />
              </EnrollButton>
              
              <ConsultationButton
                onClick={() => navigate('/contact')}
              >
                Book Free Counseling
              </ConsultationButton>
            </SidebarCard>
          </ContentGrid>
        </AnimatePresence>

      </Container>
    </PageWrapper>
  );
};

export default CareerPaths;
